import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import UserInfoModal from "../../other/UserInfoModal/UserInfoModal";
import WaiverModal from "../../other/WaiverModal/WaiverModal";
import ProfilePhotoControl from "./ProfilePhotoControl";
import MySubscriptionSection from "./MySubscriptionSection";
import SubscribeBanner from "./SubscribeBanner";
import CaptainStoryStrip from "./CaptainStoryStrip";
import { formatHostsSentence } from "../../functions/hosts";
import { executeQuery, executeMutation } from "../../../graphql/client";
import "./userdashboard.css";

const UserDashboard = ({
  userInfo: propUserInfo,
  cognitoToken: propCognitoToken,
  userRole: propUserRole,
  onLogout,
  onUserInfoUpdate,
}) => {
  const navigate = useNavigate();

  // State management
  const [localUserInfo, setLocalUserInfo] = useState(null);
  const [localToken, setLocalToken] = useState(null);
  const [localUserRole, setLocalUserRole] = useState(null);
  const [activeTab, setActiveTab] = useState("events");
  const [socialEvents, setSocialEvents] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState({});
  // Event id currently saving an RSVP (disables that card's button).
  const [rsvpBusyId, setRsvpBusyId] = useState(null);
  const [showUserInfoModal, setShowUserInfoModal] = useState(false);
  const [currentFeatureType, setCurrentFeatureType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [chats, setChats] = useState([]);
  // Waiver backfill gate: null = unknown (query pending/failed — don't
  // block), false = row has no signature -> show the blocking modal,
  // true = signed.
  const [waiverSigned, setWaiverSigned] = useState(null);
  // "?subscribed=1" return from Stripe Checkout — one-time celebration
  // banner at the top of the Events tab.
  const [justSubscribed, setJustSubscribed] = useState(false);

  // Read from session storage if props are not provided
  useEffect(() => {
    // If props are provided, use them
    if (propUserInfo && propCognitoToken && propUserRole) {
      setLocalUserInfo(propUserInfo);
      setLocalToken(propCognitoToken);
      setLocalUserRole(propUserRole);
      return;
    }

    // Otherwise, try to get from session storage
    const storedToken = sessionStorage.getItem("cognitoIdToken");
    if (!storedToken) {
      // Redirect to login if no token
      navigate("/login");
      return;
    }

    // Set token
    setLocalToken(storedToken);

    try {
      // Parse token to get basic user info
      const payload = JSON.parse(atob(storedToken.split(".")[1]));

      // Get stored profile info if available
      const storedProfileInfo = sessionStorage.getItem("userProfileInfo");
      if (storedProfileInfo) {
        const profileInfo = JSON.parse(storedProfileInfo);
        setLocalUserInfo({
          email: payload.email,
          sub: payload.sub,
          groups: payload["cognito:groups"] || [],
          phone: profileInfo.phone || "",
          sex: profileInfo.sex || "",
          shirtSize: profileInfo.shirtSize || "",
          interestedEvents:
            profileInfo.interestedEvents ||
            profileInfo.reInterestedInEvents ||
            "",
          referredBy: profileInfo.referredBy || "",
        });
      } else {
        setLocalUserInfo({
          email: payload.email,
          sub: payload.sub,
          groups: payload["cognito:groups"] || [],
        });
      }

      // Set user role
      setLocalUserRole(
        payload["cognito:groups"]?.includes("Admins")
          ? "admin"
          : payload["cognito:groups"]?.includes("Members")
          ? "member"
          : "user"
      );
    } catch (e) {
      console.error("Error parsing JWT token:", e);
      navigate("/login");
    }
  }, [propUserInfo, propCognitoToken, propUserRole, navigate]);

  // Add useEffect to load user info from storage on component mount
  useEffect(() => {
    const loadedInfo = loadUserProfileInfoFromStorage();
    console.log("Initial load of user info:", loadedInfo);

    // If we don't have stored info yet, check if we can load from props
    if (!loadedInfo && propUserInfo) {
      setLocalUserInfo(propUserInfo);
    }
  }, [propUserInfo]);

  // Use proper values from either props or local state
  const userInfo = propUserInfo || localUserInfo;
  const cognitoToken = propCognitoToken || localToken;
  const userRole = propUserRole || localUserRole;

  // Handle logout if no callback provided
  const handleLocalLogout = () => {
    // Clear tokens
    sessionStorage.removeItem("cognitoIdToken");
    sessionStorage.removeItem("userProfileInfo");

    // Redirect to login
    navigate("/login");
  };

  // The actual logout function to use
  const logoutFn = onLogout || handleLocalLogout;

  // Same public join link the admin page and legacy /join form use.
  const GROUPME_JOIN_URL = "https://groupme.com/join_group/101834766/JW8Wacte";
  const [phoneCopied, setPhoneCopied] = useState(false);

  const copyPhoneForGroupMe = async () => {
    try {
      await navigator.clipboard.writeText(userInfo?.phone || "");
      setPhoneCopied(true);
      setTimeout(() => setPhoneCopied(false), 2500);
    } catch (e) {
      // Clipboard API unavailable (old browser / non-HTTPS) — the
      // number is displayed as selectable text either way.
      console.warn("Clipboard copy failed", e);
    }
  };

  // Load social events and chats when dashboard is loaded
  useEffect(() => {
    if (cognitoToken && userInfo) {
      if (activeTab === "events") {
        loadSocialEvents();
      } else if (
        activeTab === "chats" &&
        (userRole === "member" || userRole === "admin")
      ) {
        // Only staff get the in-app chat browser; everyone else sees
        // the join-our-GroupMe panel, which needs no data load.
        loadChats();
      }
    }
  }, [activeTab, userRole, userInfo, cognitoToken]);

  // Hydrate the profile from the DATABASE once per mount. Session
  // storage only knows what THIS browser saw — someone who completed
  // signup on their phone and logs in on a laptop would otherwise be
  // re-asked questions they already answered. Also carries the
  // waiverSigned flag for the waiver backfill gate below.
  const serverProfileLoadedRef = useRef(false);
  useEffect(() => {
    if (!cognitoToken || !userInfo) return;
    if (serverProfileLoadedRef.current) return;
    serverProfileLoadedRef.current = true;
    (async () => {
      try {
        const res = await executeQuery(`
          query MyDashboardProfile {
            me {
              phone
              sex
              shirtSize
              interestedEvents
              referredBy
              waiverSigned
            }
          }
        `);
        const me = res?.me;
        if (!me) return;
        if (typeof me.waiverSigned === "boolean") {
          setWaiverSigned(me.waiverSigned);
        }
        const merged = {};
        if (me.phone) merged.phone = me.phone;
        if (me.sex) merged.sex = me.sex;
        if (me.shirtSize) merged.shirtSize = me.shirtSize;
        if (me.interestedEvents) merged.interestedEvents = me.interestedEvents;
        if (me.referredBy) merged.referredBy = me.referredBy;
        if (
          Object.keys(merged).length > 0 &&
          typeof userInfoUpdateFn === "function"
        ) {
          userInfoUpdateFn(merged);
        }
      } catch (err) {
        // Keep whatever sessionStorage had — hydration is an upgrade,
        // not a requirement, and we never BLOCK on it (waiverSigned
        // stays null -> no waiver modal on a failed fetch).
        console.warn("Profile hydrate failed (using cached values):", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cognitoToken, userInfo]);

  // One-time celebration when returning from Stripe Checkout with
  // ?subscribed=1 (the banner/section return URLs).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "1") {
      setJustSubscribed(true);
      const url = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", url);
    }
  }, []);

  // Load social events from GraphQL API
  const loadSocialEvents = async () => {
    setLoading(true);
    setError("");

    try {
      const eventsQuery = `
        query GetEvents($days: Int) {
          events(days: $days) {
            id
            title
            date
            time
            location
            privateLocation
            description
            generalpublic
            imgKey
            hosts
            isSentToGroupMe
            rsvpCount
            rsvpMaybeCount
            rsvpNames
            rsvpMaybeNames
            myRsvp
          }
        }
      `;

      const result = await executeQuery(eventsQuery, { days: 14 });

      // Debug: log the raw events data from API
      console.log("Raw events from GraphQL:", result?.events);

      if (result?.events) {
        // The dates are already formatted, but we want to reformat them to "Sun May 4" style
        const formattedEvents = result.events.map((event) => {
          console.log(
            "Processing event:",
            event.id,
            "Original date value:",
            event.date
          );

          const updatedEvent = { ...event };

          if (updatedEvent.date) {
            try {
              // Parse the existing date (e.g., "May 4, 2025")
              const dateObj = new Date(updatedEvent.date);

              // Check if this date is valid
              if (!isNaN(dateObj.getTime())) {
                // Format to "Sun May 4" (weekday, month, day)
                updatedEvent.date = dateObj.toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: undefined, // Exclude year
                });
                console.log("  Reformatted date:", updatedEvent.date);
              }
            } catch (e) {
              console.error("Error reformatting date:", e);

              // If reformatting fails, try to extract from ID as fallback
              if (updatedEvent.id && typeof updatedEvent.id === "string") {
                try {
                  const dateParts = updatedEvent.id.split("-");
                  if (dateParts.length >= 3) {
                    const [year, month, day] = dateParts;
                    // Only proceed if these look like date parts (numbers)
                    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                      const date = new Date(
                        parseInt(year, 10),
                        parseInt(month, 10) - 1,
                        parseInt(day, 10)
                      );

                      const formattedDate = date.toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: undefined, // Exclude year
                      });
                      console.log("  Created date from ID:", formattedDate);
                      updatedEvent.date = formattedDate;
                    }
                  }
                } catch (e) {
                  console.error("Error creating date from ID:", e);
                }
              }
            }
          } else if (updatedEvent.id && typeof updatedEvent.id === "string") {
            // If no date field, try to extract from ID
            try {
              const dateParts = updatedEvent.id.split("-");
              if (dateParts.length >= 3) {
                const [year, month, day] = dateParts;
                // Only proceed if these look like date parts (numbers)
                if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                  const date = new Date(
                    parseInt(year, 10),
                    parseInt(month, 10) - 1,
                    parseInt(day, 10)
                  );

                  const formattedDate = date.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                    year: undefined, // Exclude year
                  });
                  console.log("  Created date from ID:", formattedDate);
                  updatedEvent.date = formattedDate;
                }
              }
            } catch (e) {
              console.error("Error creating date from ID:", e);
            }
          }

          return updatedEvent;
        });

        console.log("Events ready for display:", formattedEvents);
        setSocialEvents(formattedEvents);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setError("Failed to load events. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Load chats from GraphQL API
  const loadChats = async () => {
    setLoading(true);
    setError("");

    try {
      const chatsQuery = `
        query GetChats {
          chats {
            id
            name
            description
            imageUrl
            createdAt
            updatedAt
            memberCount
            messageCount
          }
        }
      `;

      const result = await executeQuery(chatsQuery);

      if (result?.chats) {
        setChats(result.chats);
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
      setError("Failed to load chats. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Load user profile information from storage
  const loadUserProfileInfoFromStorage = () => {
    try {
      const storedProfileInfo = sessionStorage.getItem("userProfileInfo");
      if (storedProfileInfo) {
        const profileInfo = JSON.parse(storedProfileInfo);
        console.log("Loaded profile info from storage:", profileInfo);

        // Normalize fields by checking for various possible field names
        const normalizedInfo = {
          name:
            profileInfo.name ||
            profileInfo.username ||
            profileInfo.firstName ||
            "",
          email: profileInfo.email || profileInfo.emailAddress || "",
          phone:
            profileInfo.phone ||
            profileInfo.phoneNumber ||
            profileInfo.userPhone ||
            "",
          shirtSize:
            profileInfo.shirtSize ||
            profileInfo.shirt_size ||
            profileInfo.tshirt ||
            "",
          age: profileInfo.age || profileInfo.userAge || "",
          sex: profileInfo.sex || profileInfo.gender || "",
          interestedEvents:
            profileInfo.interestedEvents ||
            profileInfo.reInterestedInEvents ||
            [],
          // Add any other fields needed
        };

        setLocalUserInfo(normalizedInfo);
        return normalizedInfo;
      }
    } catch (e) {
      console.error("Error loading user profile from storage:", e);
    }
    return null;
  };

  // Handle user info update locally if no callback provided
  const handleLocalUserInfoUpdate = (updatedInfo) => {
    // Update local user info
    setLocalUserInfo((prev) => ({
      ...prev,
      ...updatedInfo,
    }));

    // Save to session storage
    try {
      const storedToken = sessionStorage.getItem("cognitoIdToken");
      if (storedToken) {
        // Get existing stored profile info if available
        let existingProfile = {};
        const storedProfileInfo = sessionStorage.getItem("userProfileInfo");
        if (storedProfileInfo) {
          try {
            existingProfile = JSON.parse(storedProfileInfo);
          } catch (e) {
            console.error("Error parsing existing profile info:", e);
          }
        }

        // Merge with updated info
        sessionStorage.setItem(
          "userProfileInfo",
          JSON.stringify({
            ...existingProfile,
            ...updatedInfo,
          })
        );

        // Log success for debugging
        console.log(
          "Updated user profile information saved to session storage:",
          {
            ...existingProfile,
            ...updatedInfo,
          }
        );
      }
    } catch (e) {
      console.error("Error saving user profile info to session storage:", e);
    }
  };

  // Use the provided callback or fall back to local implementation
  const userInfoUpdateFn = onUserInfoUpdate || handleLocalUserInfoUpdate;

  // Handle toggling event card expansion
  const toggleEventExpansion = (eventId) => {
    setExpandedEvents((prev) => ({
      ...prev,
      [eventId]: !prev[eventId],
    }));
  };

  // Native RSVP. Tapping the already-selected answer sends "none"
  // (clears it). The server returns the fresh roster so the card
  // updates in place without a full events refetch.
  const handleRsvp = async (event, status) => {
    if (rsvpBusyId) return;
    const effective = event.myRsvp === status ? "none" : status;
    setRsvpBusyId(event.id);
    try {
      const rsvpMutation = `
        mutation RsvpToEvent($socialEventId: ID!, $status: String!) {
          rsvpToEvent(socialEventId: $socialEventId, status: $status) {
            myRsvp
            rsvpCount
            rsvpMaybeCount
            rsvpNames
            rsvpMaybeNames
          }
        }
      `;
      const result = await executeMutation(rsvpMutation, {
        socialEventId: event.id,
        status: effective,
      });
      const r = result?.rsvpToEvent;
      if (r) {
        setSocialEvents((prev) =>
          prev.map((ev) => (ev.id === event.id ? { ...ev, ...r } : ev))
        );
      }
    } catch (err) {
      console.error("RSVP failed:", err);
      window.alert(err.message || "Couldn't save your RSVP — try again.");
    } finally {
      setRsvpBusyId(null);
    }
  };

  // Tab changes are ungated now — the Chats tab itself shows either
  // the in-app browser (staff) or the join-our-GroupMe panel (everyone
  // else), so there's nothing to interrogate the user about first.
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle user info submission. No feature-specific follow-ups any
  // more — the chats panel / banners re-render from the fresh userInfo.
  const handleUserInfoSubmit = async (userData) => {
    // Update user info in the parent component via callback
    if (typeof userInfoUpdateFn === "function") {
      userInfoUpdateFn(userData);
    }

    // Close the modal
    setShowUserInfoModal(false);
  };

  // Handle navigating to chat detail
  const handleChatSelect = (chatId) => {
    // For future implementation
    console.log("Selected chat:", chatId);
    // navigate(`/chats/${chatId}`);
  };

  // Format date to display nicely
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Show loading state while fetching user info
  if (!userInfo || !cognitoToken) {
    return <div className="loading-container">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        {userInfo && (
          <span className="user-email">
            {userInfo.email} ({userRole})
          </span>
        )}
      </header>

      <div className="dashboard-content">
        {loading && <div className="loading-spinner">Loading...</div>}

        {error && <div className="error-message">{error}</div>}

        {/* Events Tab */}
        {activeTab === "events" && (
          <div className="tab-content">
            {justSubscribed && (
              <div
                style={{
                  marginBottom: 12,
                  padding: "12px 16px",
                  background: "#eef7ee",
                  border: "1px solid #b9dcb5",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <span style={{ color: "#1f4f1d", fontSize: 14 }}>
                  🎉 <strong>You're in!</strong> Your subscription is active —
                  event locations below are now visible to you. (If one still
                  says "join group", give it a few seconds and refresh.)
                </span>
                <button
                  type="button"
                  onClick={() => setJustSubscribed(false)}
                  aria-label="Dismiss"
                  style={{
                    background: "none",
                    border: "none",
                    color: "#1f7a3a",
                    fontSize: 20,
                    cursor: "pointer",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <SubscribeBanner
              userInfo={userInfo}
              onNeedInfo={() => {
                setCurrentFeatureType("subscribe");
                setShowUserInfoModal(true);
              }}
            />
            <CaptainStoryStrip />
            {socialEvents.length === 0 && !loading ? (
              <div className="no-data">No upcoming social events found</div>
            ) : (
              <>
                <div className="events-list">
                  {socialEvents.map((event) => (
                    <div
                      key={event.id}
                      className={`event-card ${
                        !event.generalpublic ? "private" : ""
                      } ${expandedEvents[event.id] ? "expanded" : ""}`}
                      onClick={() => toggleEventExpansion(event.id)}
                    >
                      <div className="event-header">
                        <div className="event-title">
                          <h3>{event.title}</h3>
                          {/* Server only returns unposted events to
                              admins, so this badge is admin-only by
                              construction — it flags events that still
                              need posting to GroupMe. */}
                          {event.isSentToGroupMe === false && (
                            <span
                              style={{
                                display: "inline-block",
                                background: "#fff3cd",
                                color: "#7a5b00",
                                border: "1px solid #ffe08a",
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                                padding: "2px 6px",
                                marginBottom: 4,
                              }}
                            >
                              not posted to GroupMe
                            </span>
                          )}
                          <div className="event-meta">
                            <span className="event-date">{event.date}</span>
                            <span className="event-time">{event.time}</span>
                            {/* Real RSVP count (members only — the
                                server omits rsvpCount for callers
                                without standing). */}
                            {event.rsvpCount != null &&
                              (event.rsvpCount > 0 ||
                              event.rsvpMaybeCount > 0 ? (
                                <span className="event-attendance">
                                  <span className="attendance-icon">👥</span>
                                  {event.rsvpCount} going
                                  {!expandedEvents[event.id] && (
                                    <span
                                      style={{
                                        color: "#4a90e2",
                                        marginLeft: 6,
                                      }}
                                    >
                                      · see who's going
                                    </span>
                                  )}
                                </span>
                              ) : (
                                /* Nobody yet — don't advertise an empty
                                   roster; invite the first RSVP instead. */
                                !expandedEvents[event.id] && (
                                  <span className="event-attendance">
                                    <span className="attendance-icon">
                                      👥
                                    </span>
                                    <span style={{ color: "#4a90e2" }}>
                                      Be the first to RSVP
                                    </span>
                                  </span>
                                )
                              ))}
                          </div>
                          {(() => {
                            const hostsLine = formatHostsSentence(event.hosts);
                            return hostsLine ? (
                              <div className="event-hosts">{hostsLine}</div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      {event.imgKey && (
                        <div className="event-image">
                          <img
                            src={`https://s3.amazonaws.com/social-images-public/${event.imgKey}`}
                            alt={event.title}
                            loading="lazy"
                          />
                        </div>
                      )}
                      <div className="event-details">
                        <div className="event-location">
                          <span className="location-pin">📍</span>{" "}
                          {/* The server only includes privateLocation for
                              entitled callers (approved / paid / female /
                              staff), so its presence IS the access check —
                              no client-side role test on top, which was
                              hiding it from approved regular users. */}
                          {event.privateLocation
                            ? event.privateLocation
                            : event.location}
                        </div>
                        {expandedEvents[event.id] && event.description && (
                          <div className="event-description">
                            <strong>Description:</strong> {event.description}
                          </div>
                        )}
                        {/* Native RSVP: buttons + who's-going roster.
                            Only in the EXPANDED card (the collapsed
                            card just shows the count + hint), and only
                            when the server included RSVP data
                            (members). stopPropagation so tapping RSVP
                            doesn't collapse the card. */}
                        {event.rsvpCount != null && expandedEvents[event.id] && (
                          <div
                            className="event-rsvp"
                            onClick={(e) => e.stopPropagation()}
                            style={{ marginTop: 10 }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#5b667a",
                                marginBottom: 6,
                              }}
                            >
                              Are you going?
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              {[
                                { key: "yes", label: "✅ Yes" },
                                { key: "maybe", label: "🤔 Maybe" },
                                { key: "no", label: "❌ No" },
                              ].map((opt) => {
                                const selected = event.myRsvp === opt.key;
                                return (
                                  <button
                                    key={opt.key}
                                    type="button"
                                    onClick={() =>
                                      handleRsvp(event, opt.key)
                                    }
                                    disabled={rsvpBusyId === event.id}
                                    title={
                                      selected ? "Tap again to clear" : ""
                                    }
                                    style={{
                                      padding: "8px 14px",
                                      background: selected
                                        ? "#1f7a3a"
                                        : "#fff",
                                      color: selected ? "#fff" : "#3a4150",
                                      border: selected
                                        ? "1px solid #1f7a3a"
                                        : "1px solid #d0d6e0",
                                      borderRadius: 6,
                                      fontSize: 14,
                                      fontWeight: 600,
                                      cursor:
                                        rsvpBusyId === event.id
                                          ? "default"
                                          : "pointer",
                                    }}
                                  >
                                    {opt.label}
                                    {selected && " ✓"}
                                  </button>
                                );
                              })}
                            </div>
                            {event.rsvpNames && event.rsvpNames.length > 0 && (
                              <div
                                style={{
                                  marginTop: 8,
                                  fontSize: 13,
                                  color: "#3a4150",
                                }}
                              >
                                <strong>Going:</strong>{" "}
                                {event.rsvpNames.join(", ")}
                              </div>
                            )}
                            {event.rsvpMaybeNames &&
                              event.rsvpMaybeNames.length > 0 && (
                                <div
                                  style={{
                                    marginTop: 4,
                                    fontSize: 13,
                                    color: "#5b667a",
                                  }}
                                >
                                  <strong>Maybe:</strong>{" "}
                                  {event.rsvpMaybeNames.join(", ")}
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Chats Tab */}
        {activeTab === "chats" && (
          <div className="tab-content">
            {userRole === "member" || userRole === "admin" ? (
              <div className="chats-container">
                <h2>Group Chats</h2>
                {chats.length === 0 && !loading ? (
                  <div className="no-data">No chats available</div>
                ) : (
                  <div className="chats-list">
                    {chats.map((chat) => (
                      <div
                        key={chat.id}
                        className="chat-card"
                        onClick={() => handleChatSelect(chat.id)}
                      >
                        <div className="chat-avatar">
                          {chat.imageUrl ? (
                            <img src={chat.imageUrl} alt={chat.name} />
                          ) : (
                            <div className="chat-avatar-placeholder">
                              {chat.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="chat-details">
                          <h3>{chat.name}</h3>
                          {chat.description && (
                            <p className="chat-description">
                              {chat.description}
                            </p>
                          )}
                          <div className="chat-meta">
                            <span className="message-count">
                              {chat.messageCount || 0} messages
                            </span>
                            <span className="updated-at">
                              Last updated: {formatDate(chat.updatedAt * 1000)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Regular users: all the chatter lives in GroupMe, so
                 this tab is the door to it — no gating, no subscribe
                 pitch. We DO need their phone number first, because
                 GroupMe join requests are matched to accounts by the
                 exact number they gave us. */
              <div className="subscription-required">
                <div className="subscription-icon">💬</div>
                <h2>Join our GroupMe chat</h2>
                {userInfo?.phone ? (
                  <>
                    <p>
                      All the action happens in our GroupMe — event chatter,
                      who's going, carpools, and last-minute updates.
                    </p>
                    <div
                      style={{
                        background: "#fff8e1",
                        border: "1px solid #ffe2a8",
                        borderRadius: 8,
                        padding: "12px 14px",
                        margin: "12px auto",
                        maxWidth: 420,
                        textAlign: "left",
                        fontSize: 14,
                        color: "#5c4700",
                      }}
                    >
                      <strong>Important:</strong> when GroupMe asks for a
                      phone number to join our group, enter{" "}
                      <strong>exactly this number</strong>:
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          margin: "10px 0",
                        }}
                      >
                        <code
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            background: "#fff",
                            border: "1px solid #e6d49a",
                            borderRadius: 6,
                            padding: "8px 12px",
                            userSelect: "all",
                          }}
                        >
                          {userInfo.phone}
                        </code>
                        <button
                          type="button"
                          onClick={copyPhoneForGroupMe}
                          style={{
                            padding: "8px 14px",
                            background: phoneCopied ? "#1f7a3a" : "#1f6feb",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {phoneCopied ? "Copied ✓" : "Copy"}
                        </button>
                      </div>
                      It doesn't have to be the number your GroupMe account
                      uses — but it must match the number above, because
                      that's how we connect your join request to your
                      membership here.
                    </div>
                    <a
                      className="subscribe-btn-large"
                      href={GROUPME_JOIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-block",
                        textDecoration: "none",
                        textAlign: "center",
                      }}
                    >
                      Open GroupMe &amp; join
                    </a>
                    <p
                      style={{ fontSize: 13, color: "#7a8494", marginTop: 12 }}
                    >
                      GroupMe is a free app — the link opens it if you have
                      it, or takes you to download it if you don't.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      One quick step first: add your phone number. GroupMe
                      join requests are matched to your account by phone
                      number, so we need it before you head over.
                    </p>
                    <button
                      className="subscribe-btn-large"
                      onClick={() => {
                        setCurrentFeatureType("chats");
                        setShowUserInfoModal(true);
                      }}
                    >
                      Add my phone number
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Account Tab */}
        {activeTab === "account" && (
          <div className="tab-content">
            <div className="account-section">
              <div className="user-profile">
                <h2>Account Settings</h2>
                {userInfo && (
                  <div className="account-details">
                    <ProfilePhotoControl
                      userId={userInfo.sub}
                      email={userInfo.email}
                      name={userInfo.name || userInfo.email}
                    />

                    <p className="account-email">
                      <strong>Email:</strong> {userInfo.email}
                    </p>
                    <p className="account-role">
                      <strong>Role:</strong> {userRole}
                    </p>

                    <MySubscriptionSection sex={userInfo?.sex || ""} />

                    <h3 className="profile-section-title">
                      Profile Information
                    </h3>
                    <div className="profile-info-container">
                      <div className="profile-info-item">
                        <strong>Phone:</strong>{" "}
                        {userInfo.phone || "Not provided"}
                      </div>
                      <div className="profile-info-item">
                        <strong>Sex:</strong>{" "}
                        {userInfo.sex
                          ? userInfo.sex.charAt(0).toUpperCase() +
                            userInfo.sex.slice(1)
                          : "Not provided"}
                      </div>
                      <div className="profile-info-item">
                        <strong>T-Shirt Size:</strong>{" "}
                        {userInfo.shirtSize || "Not provided"}
                      </div>
                      <div className="profile-info-item">
                        <strong>Interests:</strong>{" "}
                        {userInfo.interestedEvents || "Not provided"}
                      </div>
                      <div className="profile-info-item">
                        <strong>Referral Source:</strong>{" "}
                        {userInfo.referredBy || "Not provided"}
                      </div>
                    </div>

                    <div className="account-actions">
                      <button
                        className="edit-profile-btn"
                        onClick={() => {
                          setCurrentFeatureType("profile");
                          setShowUserInfoModal(true);
                        }}
                      >
                        Edit Profile
                      </button>
                    </div>
                  </div>
                )}
                <button onClick={logoutFn} className="logout-btn">
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Waiver backfill: blocking one-time signature for accounts
            that pre-date the signup waiver step (or whose signup got
            interrupted). When the fit question is ALSO unanswered the
            modal collects it in the same screen, so completing this
            fully completes the account — a woman leaves it
            auto-approved with locations visible. */}
        {waiverSigned === false && (
          <WaiverModal
            defaultName={userInfo.name || ""}
            needSex={!(userInfo.sex === "male" || userInfo.sex === "female")}
            onSigned={(extra) => {
              setWaiverSigned(true);
              if (
                extra &&
                Object.keys(extra).length > 0 &&
                typeof userInfoUpdateFn === "function"
              ) {
                userInfoUpdateFn(extra);
              }
            }}
          />
        )}

        {/* User Info Modal */}
        {showUserInfoModal && (
          <UserInfoModal
            onClose={() => setShowUserInfoModal(false)}
            onSubmit={handleUserInfoSubmit}
            email={userInfo.email}
            userInfo={userInfo}
            cognitoToken={cognitoToken}
            featureType={currentFeatureType}
          />
        )}
      </div>

      {/* Mobile App-like Bottom Navigation */}
      <div className="mobile-tab-navigation">
        <button
          className={activeTab === "events" ? "active" : ""}
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab("events");
          }}
        >
          <span className="tab-icon">📅</span>
          <span className="tab-label">Events</span>
        </button>
        <button
          className={activeTab === "chats" ? "active" : ""}
          onClick={(e) => {
            e.stopPropagation();
            handleTabChange("chats");
          }}
        >
          <span className="tab-icon">💬</span>
          <span className="tab-label">Chats</span>
        </button>
        <button
          className={activeTab === "account" ? "active" : ""}
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab("account");
          }}
        >
          <span className="tab-icon">👤</span>
          <span className="tab-label">Account</span>
        </button>
      </div>
    </div>
  );
};

export default UserDashboard;
